import { useLanguage } from '../context/LanguageContext';
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, UserCheck } from 'lucide-react';

export default function PolitiqueConfidentialite() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-500 hover:text-[#00aaff] mb-6 transition-colors">
          <ArrowLeft size={18} />
          <span className="text-sm">Retour à l'accueil</span>
        </Link>

        <h1 className="text-3xl md:text-5xl font-black text-gray-900 mb-8">Politique de Confidentialité</h1>

        <div className="prose prose-gray max-w-none space-y-8">
          {/* Intro */}
          <section>
            <p className="text-gray-700 leading-relaxed mb-4">
              La protection de vos données personnelles est une priorité pour <strong>VOYEZ VOUS</strong>. La présente Politique de Confidentialité vous informe sur la manière dont nous collectons, utilisons et protégeons vos données personnelles conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés.
            </p>
            <p className="text-gray-700 leading-relaxed">
              En utilisant notre site joeycyan.com, vous acceptez les pratiques décrites dans cette politique.
            </p>
          </section>

          {/* Responsable du traitement */}
          <section className="bg-gray-50 p-6 rounded-xl">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Responsable du traitement des données</h2>
            <ul className="list-none space-y-2 text-gray-700 ml-0">
              <li><strong>Raison sociale :</strong> VOYEZ VOUS</li>
              <li><strong>SIRET :</strong> 830 404 588 00014</li>
              <li><strong>Adresse :</strong> 100 RUE DU FAUBOURG DU TEMPLE, 75011 PARIS</li>
              <li><strong>Email :</strong> contact@joeycyan.com</li>
            </ul>
          </section>

          {/* Données collectées */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Données personnelles collectées</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <UserCheck size={20} className="text-[#00aaff]" />
                  Données d'identification
                </h3>
                <p className="text-gray-700 leading-relaxed mb-2">Lors de la création de votre compte ou d'une commande, nous collectons :</p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  <li>Nom et prénom</li>
                  <li>Adresse email</li>
                  <li>Numéro de téléphone</li>
                  <li>Adresse de livraison et de facturation</li>
                  <li>Nom de l'entreprise (pour les comptes professionnels)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Lock size={20} className="text-[#00aaff]" />
                  Données de connexion
                </h3>
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  <li>Adresse email et mot de passe (crypté)</li>
                  <li>Adresse IP</li>
                  <li>Date et heure de connexion</li>
                  <li>Type de navigateur et système d'exploitation</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Shield size={20} className="text-[#00aaff]" />
                  Données de transaction
                </h3>
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  <li>Historique des commandes</li>
                  <li>Montants des transactions</li>
                  <li>Mode de paiement utilisé (les données bancaires sont traitées par notre prestataire de paiement Stripe et ne sont pas stockées sur nos serveurs)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Eye size={20} className="text-[#00aaff]" />
                  Données de navigation
                </h3>
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  <li>Pages visitées sur notre site</li>
                  <li>Produits consultés</li>
                  <li>Contenu du panier</li>
                  <li>Cookies (voir notre <Link to="/politique-cookies" className="text-[#00aaff] hover:underline">Politique de Cookies</Link>)</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Finalités */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Finalités du traitement</h2>
            <p className="text-gray-700 leading-relaxed mb-4">Vos données personnelles sont collectées pour les finalités suivantes :</p>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <span className="text-[#00aaff] font-bold shrink-0">✓</span>
                <span className="text-gray-700"><strong>Gestion des commandes :</strong> Traitement, préparation, expédition et suivi de vos commandes</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[#00aaff] font-bold shrink-0">✓</span>
                <span className="text-gray-700"><strong>Gestion du compte client :</strong> Création et gestion de votre espace personnel</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[#00aaff] font-bold shrink-0">✓</span>
                <span className="text-gray-700"><strong>Service client :</strong> Réponse à vos questions, réclamations et demandes SAV</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[#00aaff] font-bold shrink-0">✓</span>
                <span className="text-gray-700"><strong>Paiement :</strong> Traitement sécurisé des paiements via Stripe</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[#00aaff] font-bold shrink-0">✓</span>
                <span className="text-gray-700"><strong>Communication marketing :</strong> Envoi de newsletters, offres promotionnelles (avec votre consentement)</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[#00aaff] font-bold shrink-0">✓</span>
                <span className="text-gray-700"><strong>Amélioration du service :</strong> Analyse statistique de la fréquentation et de l'utilisation du site</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[#00aaff] font-bold shrink-0">✓</span>
                <span className="text-gray-700"><strong>Respect des obligations légales :</strong> Facturation, comptabilité, lutte contre la fraude</span>
              </div>
            </div>
          </section>

          {/* Base légale */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Base légale du traitement</h2>
            <p className="text-gray-700 leading-relaxed mb-4">Le traitement de vos données personnelles repose sur :</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><strong>L'exécution du contrat :</strong> Pour traiter vos commandes et gérer votre compte</li>
              <li><strong>Votre consentement :</strong> Pour l'envoi de communications marketing (révocable à tout moment)</li>
              <li><strong>Notre intérêt légitime :</strong> Pour améliorer nos services et prévenir la fraude</li>
              <li><strong>Le respect d'obligations légales :</strong> Pour la facturation et la comptabilité</li>
            </ul>
          </section>

          {/* Destinataires */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Destinataires des données</h2>
            <p className="text-gray-700 leading-relaxed mb-4">Vos données personnelles sont destinées à :</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Les services internes de VOYEZ VOUS (vente, marketing, service client)</li>
              <li>Nos prestataires techniques (hébergement, paiement, livraison, emailing)</li>
              <li>Les autorités légales en cas d'obligation légale</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Vos données ne sont jamais vendues à des tiers à des fins commerciales.
            </p>
          </section>

          {/* Durée de conservation */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Durée de conservation</h2>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-3">
              <p className="text-gray-700"><strong>Données de compte :</strong> Conservées pendant toute la durée de vie du compte + 3 ans après la dernière activité</p>
              <p className="text-gray-700"><strong>Données de commande :</strong> 10 ans (obligation légale comptable et fiscale)</p>
              <p className="text-gray-700"><strong>Données de paiement :</strong> 13 mois après le débit (conformément aux règles bancaires)</p>
              <p className="text-gray-700"><strong>Cookies :</strong> Maximum 13 mois (voir notre <Link to="/politique-cookies" className="text-[#00aaff] hover:underline">Politique de Cookies</Link>)</p>
            </div>
          </section>

          {/* Sécurité */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Sécurité des données</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Nous mettons en œuvre toutes les mesures techniques et organisationnelles appropriées pour protéger vos données contre la perte, l'utilisation abusive, l'accès non autorisé, la divulgation, l'altération ou la destruction :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Cryptage SSL/TLS pour les transmissions de données</li>
              <li>Hachage des mots de passe (bcrypt)</li>
              <li>Serveurs sécurisés avec pare-feu</li>
              <li>Accès restreint aux données personnelles</li>
              <li>Sauvegardes régulières</li>
            </ul>
          </section>

          {/* Droits */}
          <section className="bg-[#00aaff]/5 border-l-4 border-[#00aaff] p-6 rounded-r-xl">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Vos droits</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Conformément au RGPD et à la loi Informatique et Libertés, vous disposez des droits suivants :
            </p>
            
            <div className="space-y-3">
              <div>
                <p className="font-semibold text-gray-900">✓ Droit d'accès</p>
                <p className="text-gray-700 text-sm">Obtenir une copie de vos données personnelles</p>
              </div>
              <div>
                <p className="font-semibold text-gray-900">✓ Droit de rectification</p>
                <p className="text-gray-700 text-sm">Corriger vos données inexactes ou incomplètes</p>
              </div>
              <div>
                <p className="font-semibold text-gray-900">✓ Droit à l'effacement ("droit à l'oubli")</p>
                <p className="text-gray-700 text-sm">Demander la suppression de vos données</p>
              </div>
              <div>
                <p className="font-semibold text-gray-900">✓ Droit à la limitation du traitement</p>
                <p className="text-gray-700 text-sm">Limiter l'utilisation de vos données</p>
              </div>
              <div>
                <p className="font-semibold text-gray-900">✓ Droit à la portabilité</p>
                <p className="text-gray-700 text-sm">Recevoir vos données dans un format structuré</p>
              </div>
              <div>
                <p className="font-semibold text-gray-900">✓ Droit d'opposition</p>
                <p className="text-gray-700 text-sm">Vous opposer au traitement de vos données (notamment marketing)</p>
              </div>
              <div>
                <p className="font-semibold text-gray-900">✓ Droit de retirer votre consentement</p>
                <p className="text-gray-700 text-sm">Retirer votre consentement à tout moment</p>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed mt-6">
              <strong>Pour exercer vos droits, contactez-nous à :</strong> contact@joeycyan.com<br />
              Vous recevrez une réponse dans un délai d'1 mois maximum.
            </p>
          </section>

          {/* Réclamation */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Droit de réclamation</h2>
            <p className="text-gray-700 leading-relaxed">
              Si vous estimez que le traitement de vos données personnelles ne respecte pas la réglementation, vous avez le droit d'introduire une réclamation auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL) : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#00aaff] hover:underline">www.cnil.fr</a>
            </p>
          </section>

          {/* Transferts internationaux */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Transferts de données hors UE</h2>
            <p className="text-gray-700 leading-relaxed">
              Certains de nos prestataires (notamment Stripe pour le paiement) peuvent être situés hors de l'Union Européenne. Dans ce cas, nous nous assurons que des garanties appropriées sont en place (clauses contractuelles types, Privacy Shield, etc.) pour assurer la protection de vos données.
            </p>
          </section>

          {/* Mineurs */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Données des mineurs</h2>
            <p className="text-gray-700 leading-relaxed">
              Notre site n'est pas destiné aux personnes de moins de 16 ans. Nous ne collectons pas sciemment de données personnelles concernant des mineurs. Si vous êtes parent et que vous découvrez que votre enfant nous a fourni des données personnelles, veuillez nous contacter.
            </p>
          </section>

          {/* Modifications */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Modifications de la politique</h2>
            <p className="text-gray-700 leading-relaxed">
              Nous nous réservons le droit de modifier cette Politique de Confidentialité à tout moment. Les modifications entrent en vigueur dès leur publication sur cette page. Nous vous encourageons à consulter régulièrement cette page.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-gray-50 p-6 rounded-xl">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Pour toute question concernant cette politique ou pour exercer vos droits :
            </p>
            <ul className="list-none space-y-2 text-gray-700 ml-0">
              <li><strong>Email :</strong> contact@joeycyan.com</li>
              <li><strong>Courrier :</strong> VOYEZ VOUS, 100 RUE DU FAUBOURG DU TEMPLE, 75011 PARIS</li>
            </ul>
          </section>
        </div>

        <p className="text-sm text-gray-400 mt-12">Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
      </div>
    </div>
  );
}
